// -----------------------------------------------------------------------------
// ESC / BACKSPACE RULE (code list #3, updated 03/Oct/26 — comments #4 and #8):
//   1. close an open pop-up / menu / drawer / side panel        (layer "popup")
//   2. else go back to the page you came from, like the browser's Back button (layer "back", set by the Shell);
//      with no earlier page in the app (e.g. a link opened directly) → the parent page (A5 → A4 → A3 → A2 → A1);
//      on Projects (A1) nothing happens.
// Backspace does the same as Esc — except while typing in a field, where it deletes text as usual.
// While typing, Esc only leaves the field (in-place editors cancel their edit themselves and stop the key).
// -----------------------------------------------------------------------------
import { useLayoutEffect, useRef } from "react";

export type EscLayer = "popup" | "back";
const RANK: Record<EscLayer, number> = { popup: 2, back: 1 };

interface Entry { id: number; rank: number; run: () => boolean }
const entries: Entry[] = [];
let nextId = 1;
let installed = false;

/** Pure dispatcher (unit-tested): run handlers from highest layer / newest first until one returns true. */
export function dispatchEsc(list: Entry[] = entries): boolean {
  const ordered = [...list].sort((a, b) => b.rank - a.rank || b.id - a.id);
  for (const e of ordered) if (e.run()) return true;
  return false;
}

export function registerEsc(layer: EscLayer, run: () => boolean): () => void {
  const e = { id: nextId++, rank: RANK[layer], run };
  entries.push(e);
  return () => { const i = entries.indexOf(e); if (i >= 0) entries.splice(i, 1); };
}

export function isTyping(el: Element | null): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable || el.tagName === "TEXTAREA" || el.tagName === "SELECT") return true;
  return el.tagName === "INPUT" && !["checkbox", "radio", "button", "submit"].includes((el as HTMLInputElement).type);
}

/** Which keys act as "back" (unit-tested). */
export function isBackKey(key: string, typing: boolean): "esc" | "back" | null {
  if (key === "Escape") return typing ? "esc" : "back";
  if (key === "Backspace" && !typing) return "back";
  return null;
}

/** Installed once by the Shell. */
export function installEscKey(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("keydown", (ev) => {
    const note = (what: string) => { if (ev.key === "Escape") { const w = window as unknown as { __escLog?: string[] }; (w.__escLog ??= []).push(`${what}@${document.activeElement?.tagName}:${document.activeElement?.getAttribute("aria-label") ?? ""}`); if (w.__escLog.length > 8) w.__escLog.shift(); } };
    if (ev.defaultPrevented) { note("prevented"); return; }
    const kind = isBackKey(ev.key, isTyping(document.activeElement));
    if (kind === "esc") { note("blur"); (document.activeElement as HTMLElement).blur(); ev.preventDefault(); return; }
    if (kind === "back") { const done = dispatchEsc(); note(done ? "handled" : "unhandled"); if (done) ev.preventDefault(); }
  });
}

/** Register an Esc handler while `active`. The latest `run` is always used. */
export function useEsc(layer: EscLayer, run: () => boolean, active = true): void {
  const ref = useRef(run);
  ref.current = run;
  // v0.7.4: registered before the browser paints (layout effect), so an Esc pressed the moment a pop-up appears goes to
  // that pop-up — on a slow PC it used to fall through to "go back" and leave the page.
  useLayoutEffect(() => (active ? registerEsc(layer, () => ref.current()) : undefined), [layer, active]);
}
